import { Component, inject, OnInit, OnDestroy, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subject, takeUntil, switchMap, of, filter, take, forkJoin } from 'rxjs';

import { PeopleService } from '../../service/entity-services/people.service';
import { Person } from '../../objects/person';
import { EntityType } from '../../objects/interface-library';
import { ViewEntity } from '../../objects/view-entity';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { BaaConnectionsComponent } from '../../entity-form-components/baa-connections/baa-connections.component';
import { CoiConnectionsComponent } from '../../entity-form-components/coi-connections/coi-connections.component';
import { VendorConnectionsComponent } from '../../entity-form-components/vendor-connections/vendor-connections.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';
import { ContractConnectionsComponent } from '../../entity-form-components/contract-connections/contract-connections.component';
import { NotesComponent } from '../../entity-form-components/notes/notes.component';
import { NoteRecord } from '../../objects/note-record';

@Component({
  selector: 'app-person',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatSidenavModule,
    MatToolbarModule,
    EntityActionsSidenavComponent,
    BaaConnectionsComponent,
    CoiConnectionsComponent,
    VendorConnectionsComponent,
    AttachmentConnectionsComponent,
    CriticalDateConnectionsComponent,
    ContractConnectionsComponent,
    NotesComponent
  ],
  templateUrl: './person.component.html',
  styleUrls: ['./person.component.css']
})
export class PersonComponent extends ViewEntity implements OnInit, OnDestroy {
  public route = inject(ActivatedRoute);
  public router = inject(Router);
  public peopleService = inject(PeopleService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  currentPerson: Person | null = null;
  loading = true;
  error: string | null = null;

  // Display-friendly values
  primaryVendorName: string = '';
  primaryOwnerName: string = '';
  secondaryOwnerName: string = '';
  tertiaryOwnerName: string = '';

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editPerson(),
      disabled: false
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachment(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addCriticalDate(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Details', index: 0, icon: 'info' },
    { label: 'Contracts', index: 1, icon: 'description' },
    { label: 'BAAs', index: 2, icon: 'policy' },
    { label: 'COIs', index: 3, icon: 'verified_user' },
    { label: 'Vendors', index: 4, icon: 'business' },
    { label: 'Parties', index: 5, icon: 'groups' },
    { label: 'People', index: 6, icon: 'person' },
    { label: 'Attachments', index: 7, icon: 'attach_file' },
    { label: 'Dates', index: 8, icon: 'event' },
    { label: 'Notes', index: 9, icon: 'note' },
    { label: 'Risk Assessments', index: 10, icon: 'assessment' }
  ]);

  ngOnInit() {
    const contactNum = this.route.snapshot.paramMap.get('id');

    if (!contactNum) {
      this.error = 'No person contact number provided';
      this.loading = false;
      return;
    }

    this.loadPersonFromCache(contactNum);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPersonFromCache(contactNum: string) {
    this.loading = true;
    this.error = null;
    console.log('[Person:Diag] loadPersonFromCache called with contactNum:', contactNum);

    // Wait for people cache to load, then find the person
    this.peopleService.allPeople$
      .pipe(
        filter((people: any[]) => {
          console.log('[Person:Diag] allPeople$ emission, count:', people.length);
          return people.length > 0;
        }),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (people: Person[]) => {
          console.log('[Person:Diag] Cache loaded with', people.length, 'people. Searching for contactNum:', contactNum);
          const person = people.find(p => p.contactNum === contactNum);
          console.log('[Person:Diag] Found person:', person ? person.fullName : 'NOT FOUND');

          if (!person) {
            console.log('[Person:Diag] Available contactNums:', people.map(p => p.contactNum).join(', '));
            this.error = 'Person not found';
            this.loading = false;
            return;
          }

          this.currentPerson = person;
          this.processPersonData();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading person:', err);
          this.error = err.message || 'Failed to load person. Please try again.';
          this.loading = false;
        }
      });
  }

  processPersonData() {
    if (!this.currentPerson) return;

    // Extract display-friendly names
    // Use primaryVendorFriendlyName if available, otherwise extract from primaryVendor object
    this.primaryVendorName = this.currentPerson.primaryVendorFriendlyName ||
      this.extractVendorName(this.currentPerson.primaryVendor) ||
      'N/A';

    this.primaryOwnerName = this.extractDisplayName(
      this.currentPerson.primaryResponsibleParty,
      'friendlyName',
      'email'
    );

    // Load critical dates associated with this person
    this.refreshCriticalDates();

    // Load attachments associated with this person
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    if (this.currentPerson.contactNum) {
      this.attachmentsService.getAttachmentsForEntity(this.currentPerson.contactNum, 'Person')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentPerson?.attachments?.length) {
            // Fallback: ETL'd persons may not have parentEntityId on attachments
            this.attachmentsService.getAttachmentsByNumbers(this.currentPerson.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
                this.cdr.detectChanges();
              });
          } else {
            this.entityAttachments = [];
          }
          this.cdr.detectChanges();
        });
    } else {
      this.entityAttachments = [];
    }
  }

  private extractDisplayName(
    value: any,
    primaryKey: string,
    fallbackKey?: string
  ): string {
    if (!value) return 'N/A';

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      if (value[primaryKey]) return value[primaryKey];
      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
    }

    return 'N/A';
  }

  /**
   * Extract vendor name from various possible structures
   * Handles: wrapped {vendorData: {...}}, flat object, or returns null for string IDs
   */
  private extractVendorName(vendor: any): string | null {
    if (!vendor) return null;

    // If it's just a string ID, we can't extract a name from it
    if (typeof vendor === 'string') return null;

    if (typeof vendor === 'object') {
      // Handle wrapped structure: {vendorData: {name: "..."}, vendorNum: "..."}
      if (vendor.vendorData) {
        return vendor.vendorData.name || vendor.vendorData.friendlyName || null;
      }
      // Handle flat structure
      return vendor.name || vendor.friendlyName || null;
    }

    return null;
  }

  editPerson() {
    if (this.currentPerson?.contactNum) {
      this.router.navigate(['/securehome/new-person-full-page', this.currentPerson.contactNum]);
    }
  }

  goBack() {
    this.router.navigate(['/securehome/people']);
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  // Action methods
  addAttachment(): void {
    const entityObjectForDialog = {
      ...this.currentPerson,
      entityType: EntityType.Person
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentPerson) return;

      if (newAttachment && newAttachment.attachmentNum) {
        if (this.currentPerson.contactNum) {
          this.attachmentsService.getAttachmentsForEntity(this.currentPerson.contactNum, 'Person')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                this.entityAttachments = [...this.entityAttachments, newAttachment];
              }
            });
        }
      }
    });
  }

  addCriticalDate(): void {
    // Use the base class method from ViewEntity
    super['addCriticalDate'](
      this.currentPerson,
      'contactNum',
      (id: string, update: any) => {
        // Extract the attribute name and value from the update object
        const attribute = Object.keys(update)[0];
        const value = update[attribute];
        return this.peopleService.updatePersonAttribute(id, attribute, value);
      }
    );
  }

  refreshAttachments(): void {
    if (this.currentPerson?.contactNum) {
      this.attachmentsService.getAttachmentsForEntity(this.currentPerson.contactNum, 'Person')
        .pipe(takeUntil(this.destroy$))
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentPerson?.attachments?.length) {
            this.attachmentsService.getAttachmentsByNumbers(this.currentPerson.attachments)
              .pipe(takeUntil(this.destroy$))
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    }
  }

  /**
   * Dates for this person, looked up by the person's id (the dates table indexes
   * parentEntityId) — the same way Contract, BAA and COI load theirs — merged with
   * the legacy `criticalDates` id list on the person record. The id lookup means a
   * saved date shows even if linking it onto the person record failed.
   */
  refreshCriticalDates(): void {
    const contactNum = this.currentPerson?.contactNum;
    const legacyIds = this.currentPerson?.criticalDates ?? [];
    if (!contactNum && legacyIds.length === 0) {
      this.entityDates = [];
      this.cdrZoneless.markForCheck();
      return;
    }
    forkJoin([
      contactNum ? this.criticalDatesService.getDatesByEntityId(contactNum) : of([]),
      legacyIds.length ? this.criticalDatesService.getDatesByNumbers(legacyIds) : of([])
    ]).pipe(takeUntil(this.destroy$)).subscribe(([byEntity, byIds]) => {
      const seen = new Set<string>();
      this.entityDates = [...(byEntity || []), ...(byIds || [])].filter((d: any) => {
        const id = d?.criticalDateId || d?.criticalDateNum;
        if (!id || seen.has(id)) return !id;
        seen.add(id);
        return true;
      });
      this.cdrZoneless.markForCheck();   // zoneless: post-HTTP assignment needs an explicit notify
    });
  }

  saveNotes(notes: NoteRecord[]): void {
    if (!this.currentPerson) return;
    this.currentPerson.notes = notes;
    this.peopleService.writePerson(this.currentPerson)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to save notes:', err)
      });
  }
}
